<template>
  <div
    class="combo-box"
    :class="{ 'is-open': open, 'is-disabled': disabled }"
    v-click-outside="closeDropdown"
  >
    <div class="input-field">
      <input
        class="combo-input"
        type="text"
        :placeholder="placeholder"
        v-model="query"
        @focus="onFocus"
        @click="onClick"
        @input="onInput"
        @keydown="onKeydown"
        :disabled="disabled"
      />
      <i class="material-icons combo-arrow">arrow_drop_down</i>
      <label v-if="label" class="active combo-label">{{ label }}</label>
    </div>
    <ul v-show="open" ref="optionsList" class="combo-options">
      <li
        v-for="(opt, idx) in filteredOptions"
        :key="idx"
        ref="optionItems"
        @mousedown.prevent="select(opt)"
        :class="{
          highlight: idx === highlightIndex,
          selected: isSameValue(opt.value, value)
        }"
      >
        {{ opt.label }}
      </li>
      <li v-if="filteredOptions.length === 0" class="no-results">Nenhum resultado</li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'ComboBox',
  props: {
    options: {
      type: Array,
      default: () => []
    },
    value: null,
    placeholder: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    searchable: {
      type: Boolean,
      default: true
    },
    valueKey: {
      type: String,
      default: 'value'
    },
    labelKey: {
      type: String,
      default: 'label'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    sortOptions: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      open: false,
      query: '',
      highlightIndex: -1
    }
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el.__clickOutside__ = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event)
          }
        }

        document.addEventListener('click', el.__clickOutside__)
      },

      unbind(el) {
        document.removeEventListener('click', el.__clickOutside__)
      }
    }
  },
  computed: {
    selectedLabel() {
      const found = this.normalizedOptions.find(
        o => this.isSameValue(o.value, this.value)
      )

      return found ? found.label : ''
    },
    normalizedOptions() {
      const options = this.options.map(opt => {
        if (opt && typeof opt === 'object') {
          return { value: opt[this.valueKey], label: opt[this.labelKey] }
        }
        return { value: opt, label: String(opt) }
      })

      if (!this.sortOptions) {
        return options
      }

      return [...options].sort((a, b) => {
        const labelA = (a.label || '').trim()
        const labelB = (b.label || '').trim()

        if (labelA.toLowerCase().startsWith('selecione')) return -1
        if (labelB.toLowerCase().startsWith('selecione')) return 1
        if (labelA.toLowerCase() === 'outro') return 1
        if (labelB.toLowerCase() === 'outro') return -1

        return labelA.localeCompare(labelB, 'pt-BR', { sensitivity: 'base' })
      })
    },
    filteredOptions() {
      if (!this.searchable || !this.query) return this.normalizedOptions
      const q = this.query.toLowerCase()
      return this.normalizedOptions.filter(o => (o.label || '').toLowerCase().includes(q))
    }
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        this.syncQueryFromValue()
      }
    },
    normalizedOptions() {
      if (!this.open) {
        this.syncQueryFromValue()
      }
    },
    filteredOptions() {
      if (this.highlightIndex >= this.filteredOptions.length) {
        this.highlightIndex = this.filteredOptions.length - 1
      } else if (this.open && this.highlightIndex === -1 && this.filteredOptions.length > 0) {
        this.highlightIndex = 0
      }
    }
  },
  methods: {
    onInput() {
      if (this.disabled) return
      this.open = true
      this.highlightFirst()
    },
    onFocus() {
      if (this.disabled) return
      this.open = true
      this.query = ''
      this.highlightSelectedOrFirst()
    },
    onClick() {
      if (this.disabled || this.open) return
      this.open = true
      this.query = ''
      this.highlightSelectedOrFirst()
    },
    onKeydown(event) {
      if (this.disabled) return

      const key = event.key
      const keyCode = event.keyCode

      if (key === 'ArrowDown' || keyCode === 40) {
        event.preventDefault()
        this.moveHighlight(1)
        return
      }

      if (key === 'ArrowUp' || keyCode === 38) {
        event.preventDefault()
        this.moveHighlight(-1)
        return
      }

      if (key === 'Home' || keyCode === 36) {
        event.preventDefault()
        this.moveToEdge('first')
        return
      }

      if (key === 'End' || keyCode === 35) {
        event.preventDefault()
        this.moveToEdge('last')
        return
      }

      if (key === 'Enter' || keyCode === 13) {
        if (this.open) {
          event.preventDefault()
          this.selectHighlighted()
        }
        return
      }

      if (key === 'Escape' || keyCode === 27) {
        event.preventDefault()
        this.closeDropdown()
      }
    },
    highlightFirst() {
      this.highlightIndex = this.filteredOptions.length > 0 ? 0 : -1
      this.scrollHighlightedIntoView()
    },
    highlightSelectedOrFirst() {
      const selectedIndex = this.filteredOptions.findIndex(
        o => this.isSameValue(o.value, this.value)
      )

      if (selectedIndex >= 0) {
        this.highlightIndex = selectedIndex
        this.scrollHighlightedIntoView()
        return
      }

      this.highlightFirst()
    },
    syncQueryFromValue() {
      const found = this.normalizedOptions.find(
        o => this.isSameValue(o.value, this.value)
      )

      this.query = found ? found.label : ''
    },
    closeDropdown() {
      this.syncQueryFromValue()
      this.open = false
    },
    select(opt) {
      if (this.disabled) return
      this.$emit('input', opt.value)
      this.$emit('change', opt.value)
      this.query = opt.label
      this.open = false
    },
    moveHighlight(dir) {
      if (this.disabled) return
      if (!this.open) {
        this.open = true
        this.query = ''
        this.highlightSelectedOrFirst()
        return
      }

      const len = this.filteredOptions.length
      if (len === 0) return

      if (this.highlightIndex === -1) {
        this.highlightIndex = dir > 0 ? 0 : len - 1
      } else {
        this.highlightIndex = (this.highlightIndex + dir + len) % len
      }

      this.scrollHighlightedIntoView()
    },
    moveToEdge(edge) {
      if (this.disabled) return
      if (!this.open) {
        this.open = true
        this.query = ''
      }

      const len = this.filteredOptions.length
      if (len === 0) return

      this.highlightIndex = edge === 'first' ? 0 : len - 1
      this.scrollHighlightedIntoView()
    },
    selectHighlighted() {
      if (this.highlightIndex >= 0 && this.highlightIndex < this.filteredOptions.length) {
        this.select(this.filteredOptions[this.highlightIndex])
      }
    },
    scrollHighlightedIntoView() {
      this.$nextTick(() => {
        const list = this.$refs.optionsList
        const items = this.$refs.optionItems
        const item = Array.isArray(items) ? items[this.highlightIndex] : items

        if (!list || !item) return

        const itemTop = item.offsetTop
        const itemBottom = itemTop + item.offsetHeight
        const listTop = list.scrollTop
        const listBottom = listTop + list.clientHeight

        if (itemTop < listTop) {
          list.scrollTop = itemTop
        } else if (itemBottom > listBottom) {
          list.scrollTop = itemBottom - list.clientHeight
        }
      })
    },
    isSameValue(a, b) {
      return String(a) === String(b)
    }
  }
}
</script>

<style scoped>
.combo-box {
  position: relative;
  width: 100%;
  margin-top: -2px;
}

.combo-box .input-field {
  margin-top: 0;
  margin-bottom: 0;
}

.combo-input {
  box-sizing: border-box !important;
  padding-right: 32px !important;
  text-overflow: ellipsis;
}

.combo-label {
  color: #9e9e9e;
  font-size: 1rem;
  margin-left: -10px;
}

.input-field:focus-within .combo-label {
  color: #42A5F5;
}

.combo-arrow {
  position: absolute;
  right: 2px;
  bottom: 10px;
  color: #78909c;
  font-size: 24px;
  pointer-events: none;
  transition: transform 0.18s ease, color 0.18s ease;
}

.combo-box.is-open .combo-arrow {
  color: #29b6f6;
  transform: rotate(180deg);
}

.combo-box.is-disabled .combo-arrow {
  color: rgba(0, 0, 0, 0.26);
}

.combo-options {
  position: absolute;
  top: calc(100% - 2px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d8e1e7;
  border-radius: 3px;
  box-shadow: 0 4px 10px rgba(38, 50, 56, 0.16);
  width: 100%;
  max-height: 224px;
  overflow: auto;
  z-index: 9999;
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
}

.combo-options li {
  min-height: 40px;
  padding: 10px 38px 10px 14px;
  cursor: pointer;
  color: #263238;
  font-size: 0.95rem;
  line-height: 20px;
  position: relative;
  transition: background-color 0.14s ease, color 0.14s ease;
  word-break: break-word;
}

.combo-options li:hover {
  background-color: #d1ebef;
}

.combo-options li.highlight {
  background-color: #d1ebef;
}

.combo-options li.selected {
  background: #e3f2fd;
  color: #0277bd;
  font-weight: 600;
}

.combo-options li.selected.highlight {
  background: #d7eefc;
}

.combo-options li.selected::after {
  content: "check";
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #0288d1;
  font-family: "Material Icons";
  font-size: 18px;
  font-weight: normal;
  line-height: 1;
}

.combo-options .no-results {
  color: #78909c;
  cursor: default;
  font-style: italic;
}
</style>



// WEBPACK FOOTER //
// src/components/utils/ComboBox.vue